// src/router/routes-manifest.js
// The route table, extracted from index.js so it can be asserted in unit
// tests without instantiating the router and its guards.
/* eslint-disable */
import Home from "../views/HomePage.vue";

const routes = [
  {
    path: "/",
    name: "Home",
    component: Home,
    meta: { title: "Home" },
  },
  {
    path: "/health",
    name: "Health",
    component: () => import("../views/HealthCheck.vue"),
    meta: { title: "Health Check" },
  },
  {
    path: "/login",
    name: "Login",
    component: () => import("../views/LoginPage.vue"),
    meta: { title: "Login", requiresGuest: true },
  },
  {
    path: "/register",
    name: "Register",
    component: () => import("../views/RegisterPage.vue"),
    meta: { title: "Register", requiresGuest: true },
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: () => import("../views/ForgotPasswordPage.vue"),
    meta: { title: "Reset Password" },
  },
  {
    path: "/reset-password",
    name: "ResetPassword",
    component: () => import("../views/ResetPasswordPage.vue"),
    meta: { title: "Set New Password" },
  },
  {
    path: "/invite/:token",
    name: "InvitePage",
    component: () => import("../views/InvitePage.vue"),
    meta: { title: "You've been invited" },
  },
  {
    // Destination for cold outreach to teachers. Deliberately public and
    // unguarded: a prospect arriving from an email has no account. Carries no
    // requiresTeacher meta, so teacherGuard passes it straight through.
    path: "/for-teachers",
    name: "ForTeachers",
    component: () => import("../views/ForTeachersPage.vue"),
    meta: { title: "Teach with Crescendo" },
  },
  {
    // Landing page for the paid-search test. Unguarded: a parent arriving
    // from an ad has no account and the page promises "no sign-up". Not in
    // the nav, so the funnel reads ad traffic only.
    path: "/lessons",
    name: "LessonRequest",
    component: () => import("../views/LessonRequestPage.vue"),
    meta: { title: "Find a music teacher in east Kent" },
  },
  {
    path: "/teachers",
    name: "TeacherList",
    component: () => import("../views/TeacherList.vue"),
    meta: { title: "Find Teachers" },
  },
  {
    path: "/teachers/:id",
    name: "TeacherProfile",
    component: () => import("../views/TeacherProfile.vue"),
    meta: { title: "Teacher Profile" },
  },
  {
    path: "/onboarding",
    name: "StudentOnboarding",
    component: () => import("../views/StudentOnboardingPage.vue"),
    meta: {
      requiresAuth: true,
      title: "Get Started",
      requiresStudent: true,
    },
  },
  {
    path: "/choose-role",
    name: "ChooseRole",
    component: () => import("../views/ChooseRolePage.vue"),
    meta: {
      requiresAuth: true,
      title: "Choose your role",
      allowWithoutOnboarding: true,
    },
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: () => import("../views/DashboardPage.vue"),
    meta: { requiresAuth: true, title: "Dashboard" },
  },
  {
    path: "/dashboard/pending-approvals",
    name: "DashboardPendingApprovals",
    component: () => import("../views/DashboardPage.vue"),
    meta: { requiresAuth: true, title: "Pending Approvals" },
    beforeEnter: (to, from, next) => {
      // Redirect to dashboard with the lesson-requests tab active
      next({
        path: "/dashboard",
        query: { tab: "lesson-requests" },
      });
    },
  },
  {
    path: "/checkout",
    name: "Checkout",
    component: () => import("../views/CheckoutView.vue"),
    meta: {
      requiresAuth: true,
      title: "Checkout",
      allowWithoutOnboarding: true,
    },
  },
  {
    path: "/welcome",
    name: "StudentWelcome",
    component: () => import("../views/StudentWelcome.vue"),
    meta: { title: "Welcome to Crescendo" },
  },
  {
    path: "/messages",
    name: "Messages",
    component: () => import("../views/MessageCenter.vue"),
    meta: {
      requiresAuth: true,
      title: "Messages",
      allowWithoutOnboarding: true,
    },
  },
  {
    path: "/messages/:conversationId",
    name: "Conversation",
    component: () => import("../views/MessageCenter.vue"),
    meta: {
      requiresAuth: true,
      title: "Messages",
      allowWithoutOnboarding: true,
    },
  },
  {
    path: "/group-lessons",
    name: "GroupLessons",
    component: () => import("../views/GroupLessonsPage.vue"),
    meta: { requiresAuth: true, title: "Group Lessons" },
  },
  {
    path: "/group-lessons/create",
    name: "CreateGroupLesson",
    component: () => import("../views/CreateGroupLessonPage.vue"),
    meta: { requiresAuth: true, title: "Create Group Lesson" },
  },
  {
    path: "/group-lessons/:id",
    name: "GroupLessonDetails",
    component: () => import("../views/GroupLessonDetailsPage.vue"),
    meta: { requiresAuth: true, title: "Group Lesson Details" },
  },
  {
    path: "/admin",
    name: "Admin",
    component: () => import("../views/AdminPage.vue"),
    meta: {
      requiresAuth: true,
      requiresAdmin: true, // Add this flag for admin-only routes
      title: "Admin Dashboard",
    },
  },
  {
    path: "/admin/badges",
    name: "AdminBadges",
    component: () => import("../views/AdminBadgesPage.vue"),
    meta: {
      requiresAuth: true,
      requiresAdmin: true,
      title: "Badge Management",
    },
  },
  {
    path: "/resources/:id",
    name: "ResourceDetail",
    component: () => import("../views/ResourceDetailPage.vue"),
    meta: { title: "Resource Details" },
  },
  {
    path: "/about",
    name: "About",
    component: () => import("../views/AboutPage.vue"),
    meta: { title: "About" },
  },
  {
    path: "/terms",
    name: "Terms",
    component: () => import("../views/TermsPage.vue"),
    meta: { title: "Terms" },
  },
  {
    path: "/privacy",
    name: "PrivacyPolicy",
    component: () => import("../views/PrivacyPage.vue"),
    meta: { title: "Privacy Policy" },
  },
  {
    path: "/contact",
    name: "Contact",
    component: () => import("../views/ContactPage.vue"),
    meta: { title: "Contact" },
  },
  // ── Six-feature programme (2026-07). Each view is a Phase 0 placeholder;
  // the owning feature phase replaces the component body, not this entry.
  {
    path: "/studio",
    name: "TeacherStudio",
    component: () => import("../views/TeacherStudioPage.vue"),
    meta: {
      title: "Teacher Studio",
      requiresAuth: true,
      requiresTeacher: true,
    },
  },
  {
    path: "/studio/student/:studioStudentId",
    name: "StudioStudent",
    component: () => import("../views/StudioStudentPage.vue"),
    meta: {
      title: "Student record",
      requiresAuth: true,
      requiresTeacher: true,
    },
  },
  {
    path: "/teacher/terms",
    name: "TermsManagement",
    component: () => import("../views/TermsManagementPage.vue"),
    meta: { title: "Terms", requiresAuth: true },
  },
  {
    path: "/terms/:termId/book",
    name: "TermCheckout",
    component: () => import("../views/TermCheckoutPage.vue"),
    meta: { title: "Book a term", requiresAuth: true },
  },
  {
    path: "/teacher/earnings",
    name: "Earnings",
    component: () => import("../views/EarningsPage.vue"),
    meta: { title: "Earnings", requiresAuth: true },
  },
  {
    path: "/admin/verification",
    name: "AdminVerificationQueue",
    component: () => import("../views/AdminVerificationQueuePage.vue"),
    meta: {
      title: "Verification queue",
      requiresAuth: true,
      requiresAdmin: true,
    },
  },
  {
    path: "/teacher/calendar",
    name: "CalendarSettings",
    component: () => import("../views/CalendarSettingsPage.vue"),
    meta: { title: "Calendar", requiresAuth: true },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("../views/NotFoundPage.vue"),
    meta: { title: "Page Not Found" },
  },
];

export default routes;
